import { chain, isUndefined } from "lodash";
import { isLivingEntryRoute, isLivingOptionRoute, isSubsectionOrPropertyRoute } from "@/utils/livingRoute";

const subsectionRoutes = ["live-display-homes", "live-display-homes-property"];
const hasFilterValue = (value) => value !== "" && value !== null && typeof value !== "undefined";

const getProductCarouselFilterEntity = (rootGetters) => {
  const slides = rootGetters.activePropertySlides || [];

  return {
    filter: {
      dwellingBeds: slides.map((slide) => slide.beds).filter(hasFilterValue),
      dwellingBaths: slides.map((slide) => slide.baths).filter(hasFilterValue),
      dwellingCars: slides.map((slide) => slide.cars).filter(hasFilterValue),
      dwellingDisplayNames: slides.filter((slide) => slide.name).map((slide) => ({ en: slide.name })),
      propertyTypes: slides.map((slide) => slide.productType).filter(hasFilterValue),
      propertyViews: slides.filter((slide) => slide.viewLabel).map((slide) => slide.viewLabel),
      sizeRange: slides.map((slide) => slide.lotSize).filter(hasFilterValue),
      lotPriceRange: slides.map((slide) => slide.price).filter(hasFilterValue),
      dwellingSizeRange: slides.map((slide) => slide.dwellingSize).filter(hasFilterValue),
      dwellingPriceRange: slides.map((slide) => slide.price).filter(hasFilterValue),
    },
  };
};

const getFilterEntities = (route, rootGetters) => {
  const routeName = route.name;

  if (isLivingEntryRoute(routeName)) return rootGetters["property/activeLivingEntryOptions"] || [];
  if (isLivingOptionRoute(routeName)) return rootGetters["property/activeSubsections"] || [];

  if (route.path.includes("/product/carousel")) {
    return [getProductCarouselFilterEntity(rootGetters)];
  }

  if (isSubsectionOrPropertyRoute(routeName) || subsectionRoutes.includes(routeName)) {
    const subsection = rootGetters["property/activeSubsection"];
    return subsection ? [subsection] : [];
  }

  return [];
};

const getFilterValues = (route, rootGetters, key) => {
  return getFilterEntities(route, rootGetters)
    .map((entity) => entity && entity.filter && entity.filter[key])
    .filter(Array.isArray)
    .flat();
};

const getRangeLimits = (values, scale) => {
  if (!values.length) return [0, 0];

  const numericValues = values.map((value) => Number(value)).filter((value) => Number.isFinite(value));
  if (!numericValues.length) return [0, 0];

  const rangeMin = Math.min(...numericValues);
  const rangeMax = Math.max(...numericValues);

  if (rangeMin === rangeMax) return [0, 0];

  const rangeMinScaled = Math.floor(rangeMin / scale) * scale;
  const rangeMaxScaled = Math.ceil(rangeMax / scale) * scale;

  return [rangeMinScaled, rangeMaxScaled];
};

export default {
  namespaced: true,
  state: {
    refresh: 0,
    inputBeds: [false],
    inputBaths: [false],
    inputCars: [false],
    inputLotSize: [0, 0],
    inputLotPrice: [0, 0],
    inputDwellingSize: [0, 0],
    inputDwellingPrice: [0, 0],
    inputLotNumber: "",
    inputBuilder: "",
    inputColourScheme: "",
    inputHouseDesign: "",
    inputType: [false],
    inputView: false,
    inputStatus: false,
    inputDisplayName: false,
  },

  getters: {
    inputBeds: (state) => state.inputBeds,
    inputBaths: (state) => state.inputBaths,
    inputCars: (state) => state.inputCars,
    inputType: (state) => state.inputType,
    inputView: (state) => state.inputView,
    inputStatus: (state) => state.inputStatus,
    inputDisplayName: (state) => state.inputDisplayName,
    inputHouseDesign: (state) => state.inputHouseDesign,
    inputLotSize: (state) => state.inputLotSize,
    inputLotPrice: (state) => state.inputLotPrice,
    inputDwellingSize: (state) => state.inputDwellingSize,
    inputDwellingPrice: (state) => state.inputDwellingPrice,

    bedOptions: (state, getters, rootState, rootGetters) => {
      const beds = getFilterValues(rootState.route, rootGetters, "dwellingBeds");
      const alphaValues = beds.filter((value) => !parseInt(value));
      const numericValues = beds.filter((value) => parseInt(value));

      const alphaSorted = [...new Set(alphaValues)].sort();
      const numericSorted = [...new Set(numericValues)].sort();

      return [...alphaSorted, ...numericSorted];
    },

    bathOptions: (state, getters, rootState, rootGetters) => {
      const baths = getFilterValues(rootState.route, rootGetters, "dwellingBaths");
      const alphaValues = baths.filter((value) => !parseInt(value));
      const numericValues = baths.filter((value) => parseInt(value));

      const alphaSorted = [...new Set(alphaValues)].sort();
      const numericSorted = [...new Set(numericValues)].sort();

      return [...alphaSorted, ...numericSorted];
    },

    carOptions: (state, getters, rootState, rootGetters) => {
      const cars = getFilterValues(rootState.route, rootGetters, "dwellingCars");
      const alphaValues = cars.filter((value) => !parseInt(value));
      const numericValues = cars.filter((value) => parseInt(value));

      const alphaSorted = [...new Set(alphaValues)].sort();
      const numericSorted = [...new Set(numericValues)].sort();

      return [...alphaSorted, ...numericSorted];
    },

    typeOptions: (state, getters, rootState, rootGetters) => {
      const types = getFilterValues(rootState.route, rootGetters, "propertyTypes")
        .map((type) => type && type.toString().toLowerCase().trim())
        .filter(Boolean);

      return [...new Set(types)].sort();
    },

    viewOptions: (state, getters, rootState, rootGetters) => {
      const views = getFilterValues(rootState.route, rootGetters, "propertyViews")
        .filter((view) => view && view.en)
        .map((view) => ({ ...view, en: view.en.toLowerCase().trim() }));

      return chain(views).uniqBy("en").sortBy("en").value();
    },

    statusOptions: (state, getters, rootState, rootGetters) => {
      const statuses = getFilterValues(rootState.route, rootGetters, "propertyStatuses").filter((status) => status && status.en);

      return chain(statuses).uniqBy("en").sortBy("en").value();
    },

    displayNameOptions: (state, getters, rootState, rootGetters) => {
      const displayNames = getFilterValues(rootState.route, rootGetters, "dwellingDisplayNames")
        .filter((displayName) => displayName && displayName.en)
        .map((displayName) => ({ ...displayName, en: displayName.en.toLowerCase().trim() }));

      return chain(displayNames).uniqBy("en").sortBy("en").value();
    },

    houseDesignOptions: (state, getters, rootState, rootGetters) => {
      const houseDesigns = getFilterValues(rootState.route, rootGetters, "dwellingHouseDesigns")
        .filter((houseDesign) => houseDesign && houseDesign.en)
        .map((houseDesign) => ({ ...houseDesign, en: houseDesign.en.toLowerCase().trim() }));

      return chain(houseDesigns).uniqBy("en").sortBy("en").value();
    },

    lotSizeLimits: (state, getters, rootState, rootGetters) => {
      const range = getFilterValues(rootState.route, rootGetters, "sizeRange");
      return getRangeLimits(range, 10);
    },

    lotPriceLimits: (state, getters, rootState, rootGetters) => {
      const range = getFilterValues(rootState.route, rootGetters, "lotPriceRange");
      return getRangeLimits(range, 1000);
    },

    dwellingSizeLimits: (state, getters, rootState, rootGetters) => {
      const range = getFilterValues(rootState.route, rootGetters, "dwellingSizeRange");
      return getRangeLimits(range, 10);
    },

    dwellingPriceLimits: (state, getters, rootState, rootGetters) => {
      const range = getFilterValues(rootState.route, rootGetters, "dwellingPriceRange");
      return getRangeLimits(range, 1000);
    },
  },

  mutations: {
    SET_FILTER_PARAMETERS: (state, parameters) => {
      if (!isUndefined(parameters.inputBeds)) state.inputBeds = parameters.inputBeds;
      if (!isUndefined(parameters.inputBaths)) state.inputBaths = parameters.inputBaths;
      if (!isUndefined(parameters.inputCars)) state.inputCars = parameters.inputCars;
      if (!isUndefined(parameters.inputLotSize)) state.inputLotSize = parameters.inputLotSize;
      if (!isUndefined(parameters.inputLotPrice)) state.inputLotPrice = parameters.inputLotPrice;
      if (!isUndefined(parameters.inputDwellingSize)) state.inputDwellingSize = parameters.inputDwellingSize;
      if (!isUndefined(parameters.inputDwellingPrice)) state.inputDwellingPrice = parameters.inputDwellingPrice;
      if (!isUndefined(parameters.inputLotNumber)) state.inputLotNumber = parameters.inputLotNumber;
      if (!isUndefined(parameters.inputBuilder)) state.inputBuilder = parameters.inputBuilder;
      if (!isUndefined(parameters.inputColourScheme)) state.inputColourScheme = parameters.inputColourScheme;
      if (!isUndefined(parameters.inputHouseDesign)) state.inputHouseDesign = parameters.inputHouseDesign;
      if (!isUndefined(parameters.inputType)) state.inputType = parameters.inputType;
      if (!isUndefined(parameters.inputView)) state.inputView = parameters.inputView;
      if (!isUndefined(parameters.inputStatus)) state.inputStatus = parameters.inputStatus;
      if (!isUndefined(parameters.inputDisplayName)) state.inputDisplayName = parameters.inputDisplayName;
      if (!isUndefined(parameters.refresh)) state.refresh = parameters.refresh;
    },
  },
};
